import React, { useState, useRef, useEffect, useCallback } from "react";
import { FaBuilding, FaFloppyDisk, FaCamera } from "react-icons/fa6";
import {
    FiLoader,
    FiCheck,
    FiTrash2,
    FiMaximize2,
    FiX,
    FiZoomIn,
    FiZoomOut,
    FiCrop,
} from "react-icons/fi";
import Cropper from "react-easy-crop";

import { formatSize } from "../../../../../utilities/image_configuration/formatSize";
import { ProfileService } from "../../../../../services/ProfileService";
import { BaseUrl } from "../../../../../utilities/BaseUrl";

const inputClass =
    "w-full px-4 py-3 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 placeholder-slate-500 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all disabled:bg-slate-900/40 disabled:text-slate-500 disabled:cursor-not-allowed";

export const TabCompanySetting = ({ data, refreshData }) => {
    // ============================================================
    // COMPANY DATA
    // ============================================================

    const [companyData, setCompanyData] = useState({
        name: "",
        shortname: "",
        address: "",
        logo: "",
    });

    // ============================================================
    // UI & LOADING
    // ============================================================

    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const [fetchingData, setFetchingData] = useState(true);

    // ============================================================
    // LOGO
    // ============================================================

    const [logoPreview, setLogoPreview] = useState(null);
    const [logoFile, setLogoFile] = useState(null);

    // ============================================================
    // DRAG & DROP / COMPRESS
    // ============================================================

    const [isDragging, setIsDragging] = useState(false);
    const [isCompressing, setIsCompressing] = useState(false);
    const [compressInfo, setCompressInfo] = useState(null);

    // ============================================================
    // CROP
    // ============================================================

    const [cropModal, setCropModal] = useState(false);
    const [cropImage, setCropImage] = useState(null);

    const [crop, setCrop] = useState({
        x: 0,
        y: 0,
    });

    const [zoom, setZoom] = useState(1);

    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

    // ============================================================
    // FULLSCREEN
    // ============================================================

    const [fullscreenImage, setFullscreenImage] = useState(null);

    // ============================================================
    // REFS
    // ============================================================

    const dropZoneRef = useRef(null);
    const fileInputRef = useRef(null);

    // ============================================================
    // INITIAL DATA
    // ============================================================

    useEffect(() => {
        if (data) {
            setCompanyData({
                name: data?.name || "",
                shortname: data?.shortname || "",
                address: data?.address || "",
                logo: data?.logo || "",
            });

            if (data?.logo) {
                setLogoPreview(BaseUrl + "/storage/" + data.logo);
            }

            setFetchingData(false);
        }
    }, [data]);

    // ============================================================
    // CREATE CROPPED IMAGE
    // ============================================================

    const createCroppedImage = async (imageSrc, pixelCrop) => {
        const image = new Image();

        image.crossOrigin = "anonymous";

        image.src = imageSrc;

        await new Promise((resolve, reject) => {
            image.onload = resolve;
            image.onerror = reject;
        });

        const canvas = document.createElement("canvas");

        const ctx = canvas.getContext("2d");

        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;

        ctx.drawImage(
            image,
            pixelCrop.x,
            pixelCrop.y,
            pixelCrop.width,
            pixelCrop.height,
            0,
            0,
            pixelCrop.width,
            pixelCrop.height
        );

        return new Promise((resolve) => {
            canvas.toBlob(
                (blob) => {
                    resolve(blob);
                },
                "image/jpeg",
                0.92
            );
        });
    };

    // ============================================================
    // CROP COMPLETE
    // ============================================================

    const onCropComplete = useCallback((_, croppedPixels) => {
        setCroppedAreaPixels(croppedPixels);
    }, []);

    // ============================================================
    // PROCESS FILE
    // ============================================================

    const processFile = (file) => {
        if (!file || !file.type.startsWith("image/")) {
            alert("Hanya file gambar yang diperbolehkan.");
            return;
        }

        const reader = new FileReader();

        reader.onload = () => {
            setCropImage(reader.result);

            setCrop({
                x: 0,
                y: 0,
            });

            setZoom(1);

            setCropModal(true);
        };

        reader.readAsDataURL(file);
    };

    // ============================================================
    // APPLY CROP
    // ============================================================

    const handleApplyCrop = async () => {
        if (!cropImage || !croppedAreaPixels) {
            return;
        }

        try {
            setIsCompressing(true);

            const blob = await createCroppedImage(
                cropImage,
                croppedAreaPixels
            );

            if (!blob) {
                throw new Error("Gagal melakukan crop gambar.");
            }

            const croppedFile = new File(
                [blob],
                "company-logo.jpg",
                {
                    type: "image/jpeg",
                    lastModified: Date.now(),
                }
            );

            const previewUrl = URL.createObjectURL(blob);

            const originalSize = blob.size;

            setLogoPreview(previewUrl);
            setLogoFile(croppedFile);

            setCompressInfo({
                originalSize: originalSize,
                compressedSize: originalSize,
                percent: 0,
            });

            setCropModal(false);
            setCropImage(null);
        } catch (error) {
            console.error(error);
            alert("Gagal melakukan crop gambar.");
        } finally {
            setIsCompressing(false);
        }
    };

    // ============================================================
    // CANCEL CROP
    // ============================================================

    const handleCancelCrop = () => {
        setCropModal(false);
        setCropImage(null);

        setCrop({
            x: 0,
            y: 0,
        });

        setZoom(1);
    };

    // ============================================================
    // DRAG & DROP
    // ============================================================

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();

        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();

        setIsDragging(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();

        setIsDragging(false);

        if (
            e.dataTransfer.files &&
            e.dataTransfer.files[0]
        ) {
            processFile(e.dataTransfer.files[0]);
        }
    };

    // ============================================================
    // FILE INPUT
    // ============================================================

    const handleFileInputChange = (e) => {
        if (
            e.target.files &&
            e.target.files[0]
        ) {
            processFile(e.target.files[0]);
        }
    };

    // ============================================================
    // REMOVE LOGO
    // ============================================================

    const handleRemoveLogo = () => {
        setLogoPreview(null);
        setLogoFile(null);
        setCompressInfo(null);

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    // ============================================================
    // FULLSCREEN
    // ============================================================

    const handleOpenFullscreen = (e) => {
        e.stopPropagation();

        if (!logoPreview) {
            return;
        }

        setFullscreenImage(logoPreview);
    };

    const handleCloseFullscreen = () => {
        setFullscreenImage(null);
    };

    // ============================================================
    // ESC FULLSCREEN
    // ============================================================

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                setFullscreenImage(null);

                if (cropModal) {
                    handleCancelCrop();
                }
            }
        };

        window.addEventListener(
            "keydown",
            handleKeyDown
        );

        return () => {
            window.removeEventListener(
                "keydown",
                handleKeyDown
            );
        };
    }, [cropModal]);

    // ============================================================
    // SUBMIT
    // ============================================================

    const handleSubmit = async (e) => {
        e.preventDefault();

        setLoadingSubmit(true);

        try {
            const formData = new FormData();

            formData.append(
                "name",
                companyData.name
            );

            formData.append(
                "shortname",
                companyData.shortname
            );

            formData.append(
                "address",
                companyData.address
            );

            if (logoFile) {
                formData.append(
                    "logo",
                    logoFile
                );
            }

            await ProfileService.updateCompanyProfile({
                data: formData,
            });

            alert(
                "Informasi perusahaan berhasil diperbarui!"
            );

            if (refreshData) {
                refreshData();
            }

            setLogoFile(null);
            setCompressInfo(null);
        } catch (err) {
            console.error(err);

            alert(
                "Gagal memperbarui perusahaan."
            );
        } finally {
            setLoadingSubmit(false);
        }
    };

    // ============================================================
    // COMPANY CHANGE
    // ============================================================

    const handleCompanyChange = (e) => {
        const {
            id,
            value,
        } = e.target;

        setCompanyData((prev) => ({
            ...prev,
            [id]: value,
        }));
    };

    // ============================================================
    // LOADING
    // ============================================================

    if (fetchingData) {
        return (
            <div className="flex items-center justify-center h-64">
                <FiLoader className="animate-spin text-cyan-400 text-3xl" />
            </div>
        );
    }

    // ============================================================
    // RENDER
    // ============================================================

    return (
        <>
            <section className="space-y-6 animate-in fade-in duration-300">

                {/* ==================================================
                    HEADER
                ================================================== */}

                <header>
                    <h3 className="text-lg font-bold text-gray-100">
                        Informasi Perusahaan
                    </h3>

                    <p className="text-xs text-slate-500 mt-1">
                        Data ini akan tampil di kwitansi,
                        invoice, dan nota pelanggan.
                    </p>
                </header>

                {/* ==================================================
                    LOGO
                ================================================== */}

                <div className="flex flex-col items-center gap-4 pb-8 border-b border-slate-700/40">

                    <div className="relative group">

                        {/* ==========================================
                            UPLOAD AREA
                        ========================================== */}

                        <div
                            ref={dropZoneRef}
                            onDragEnter={handleDragEnter}
                            onDragLeave={handleDragLeave}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            onClick={() =>
                                !isCompressing &&
                                fileInputRef.current?.click()
                            }
                            className={`
                                relative
                                w-40
                                h-28
                                rounded-2xl
                                border-2
                                border-dashed
                                flex
                                flex-col
                                items-center
                                justify-center
                                cursor-pointer
                                overflow-hidden
                                transition-all
                                duration-300
                                bg-slate-800/30

                                ${
                                    isDragging
                                        ? "border-cyan-500 bg-cyan-500/10 scale-105"
                                        : logoPreview
                                        ? "border-slate-600/50"
                                        : "border-slate-600/50 hover:border-cyan-500/50 hover:bg-slate-800/50"
                                }
                            `}
                        >

                            {/* ======================================
                                LOADING
                            ====================================== */}

                            {isCompressing && (
                                <div className="absolute inset-0 z-30 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center">
                                    <FiLoader className="animate-spin text-cyan-400 text-2xl mb-2" />

                                    <span className="text-[10px] font-bold text-cyan-400">
                                        Memproses...
                                    </span>
                                </div>
                            )}

                            {/* ======================================
                                IMAGE
                            ====================================== */}

                            {logoPreview ? (
                                <img
                                    src={logoPreview}
                                    alt="Logo Preview"
                                    className="w-full h-full object-contain p-2 z-10"
                                />
                            ) : (
                                <div className="flex flex-col items-center text-slate-500 group-hover:text-cyan-400 transition-colors z-10">
                                    <FaBuilding className="text-3xl mb-2" />

                                    <span className="text-[10px] font-semibold uppercase tracking-wider text-center px-2">
                                        Upload / Drag & Drop
                                    </span>
                                </div>
                            )}

                            {/* ======================================
                                HOVER ACTION
                            ====================================== */}

                            {logoPreview &&
                                !isCompressing && (
                                    <div
                                        className="
                                            absolute
                                            inset-0
                                            z-20
                                            bg-black/60
                                            opacity-0
                                            group-hover:opacity-100
                                            transition-opacity
                                            flex
                                            items-center
                                            justify-center
                                            gap-4
                                        "
                                        onClick={(e) =>
                                            e.stopPropagation()
                                        }
                                    >

                                        {/* FULLSCREEN */}

                                        <button
                                            type="button"
                                            title="Lihat gambar"
                                            onClick={
                                                handleOpenFullscreen
                                            }
                                            className="
                                                w-10
                                                h-10
                                                rounded-xl
                                                bg-white/10
                                                hover:bg-white/20
                                                border
                                                border-white/20
                                                flex
                                                items-center
                                                justify-center
                                                text-white
                                                transition
                                            "
                                        >
                                            <FiMaximize2 size={18} />
                                        </button>

                                        {/* CHANGE IMAGE */}

                                        <button
                                            type="button"
                                            title="Ganti gambar"
                                            onClick={() =>
                                                fileInputRef.current?.click()
                                            }
                                            className="
                                                w-10
                                                h-10
                                                rounded-xl
                                                bg-cyan-500/20
                                                hover:bg-cyan-500/30
                                                border
                                                border-cyan-400/30
                                                flex
                                                items-center
                                                justify-center
                                                text-cyan-300
                                                transition
                                            "
                                        >
                                            <FaCamera size={17} />
                                        </button>
                                    </div>
                                )}

                            {/* ======================================
                                FILE INPUT
                            ====================================== */}

                            <input
                                type="file"
                                ref={fileInputRef}
                                className="hidden"
                                accept="image/png,image/jpeg,image/jpg,image/webp"
                                onChange={
                                    handleFileInputChange
                                }
                            />
                        </div>
                    </div>

                    {/* =================================================
                        INFO / BUTTON
                    ================================================= */}

                    <div className="w-full max-w-xs flex flex-col items-center gap-3">

                        {compressInfo && (
                            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">

                                <FiCheck size={14} />

                                <div className="flex flex-col text-center leading-tight">

                                    <span className="text-[10px] font-bold">
                                        Gambar siap digunakan
                                    </span>

                                    <span className="text-[9px] opacity-80">
                                        {formatSize(
                                            compressInfo.compressedSize
                                        )}
                                    </span>

                                </div>
                            </div>
                        )}

                        {logoPreview && (
                            <div className="flex items-center gap-2">

                                {/* FULLSCREEN BUTTON */}

                                <button
                                    type="button"
                                    onClick={
                                        handleOpenFullscreen
                                    }
                                    className="
                                        text-xs
                                        text-cyan-400
                                        flex
                                        items-center
                                        gap-1.5
                                        hover:text-cyan-300
                                        transition-colors
                                        border
                                        border-cyan-500/20
                                        hover:bg-cyan-500/10
                                        px-3
                                        py-1.5
                                        rounded-lg
                                    "
                                >
                                    <FiMaximize2 size={12} />

                                    Lihat Gambar
                                </button>

                                {/* REMOVE */}

                                <button
                                    type="button"
                                    onClick={
                                        handleRemoveLogo
                                    }
                                    className="
                                        text-xs
                                        text-red-400
                                        flex
                                        items-center
                                        gap-1.5
                                        hover:text-red-300
                                        transition-colors
                                        border
                                        border-red-500/20
                                        hover:bg-red-500/10
                                        px-3
                                        py-1.5
                                        rounded-lg
                                    "
                                >
                                    <FiTrash2 size={12} />

                                    Hapus
                                </button>
                            </div>
                        )}

                        {!logoPreview &&
                            !isCompressing && (
                                <p className="text-[10px] text-slate-600 flex items-center gap-1">
                                    <span className="px-1.5 py-0.5 bg-slate-800 rounded border border-slate-700 font-mono">
                                        Ctrl+V
                                    </span>

                                    untuk paste gambar
                                </p>
                            )}
                    </div>
                </div>

                {/* ==================================================
                    FORM
                ================================================== */}

                <form
                    id="companyForm"
                    onSubmit={handleSubmit}
                    className="grid grid-cols-1 md:grid-cols-2 gap-5"
                >

                    {/* COMPANY NAME */}

                    <div className="md:col-span-2">

                        <label
                            htmlFor="name"
                            className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider"
                        >
                            Nama Perusahaan
                        </label>

                        <input
                            id="name"
                            type="text"
                            value={companyData.name}
                            onChange={
                                handleCompanyChange
                            }
                            placeholder="PT. Contoh Perusahaan"
                            className={inputClass}
                        />
                    </div>

                    {/* SHORT NAME */}

                    <div>

                        <label
                            htmlFor="shortname"
                            className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider"
                        >
                            Nama Pendek
                        </label>

                        <input
                            id="shortname"
                            type="text"
                            value={
                                companyData.shortname
                            }
                            onChange={
                                handleCompanyChange
                            }
                            placeholder="CONTOH"
                            className={inputClass}
                        />
                    </div>

                    <div />

                    {/* ADDRESS */}

                    <div className="md:col-span-2">

                        <label
                            htmlFor="address"
                            className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider"
                        >
                            Alamat Lengkap
                        </label>

                        <textarea
                            id="address"
                            rows={3}
                            value={
                                companyData.address
                            }
                            onChange={
                                handleCompanyChange
                            }
                            placeholder="Masukkan alamat lengkap perusahaan..."
                            className={`${inputClass} resize-none`}
                        />
                    </div>

                </form>
            </section>

            {/* ======================================================
                SAVE BUTTON
            ====================================================== */}

            <div
                className="px-6 pt-4 flex justify-end"
                style={{
                    borderTop:
                        "1px solid rgba(51, 65, 85, 0.3)",
                }}
            >
                <button
                    form="companyForm"
                    type="submit"
                    disabled={loadingSubmit}
                    className="
                        flex
                        items-center
                        gap-2
                        px-6
                        py-2.5
                        rounded-xl
                        text-sm
                        font-bold
                        text-white
                        transition-all
                        duration-200
                        disabled:opacity-50
                        disabled:cursor-not-allowed
                    "
                    style={{
                        background:
                            "linear-gradient(135deg, #0077B6, #00B4D8)",
                        boxShadow:
                            "0 4px 15px rgba(0, 180, 216, 0.2)",
                    }}
                >
                    <FaFloppyDisk
                        className={
                            loadingSubmit
                                ? "animate-spin"
                                : ""
                        }
                    />

                    {loadingSubmit
                        ? "Menyimpan..."
                        : "Simpan Perubahan"}
                </button>
            </div>

            {/* ======================================================
                CROP MODAL
            ====================================================== */}

            {cropModal && cropImage && (
                <div className="fixed inset-0 z-[9998] bg-black/80 backdrop-blur-md flex items-center justify-center p-4">

                    <div
                        className="
                            w-full
                            max-w-2xl
                            bg-slate-900
                            border
                            border-slate-700/70
                            rounded-2xl
                            overflow-hidden
                            shadow-2xl
                        "
                    >

                        {/* HEADER */}

                        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700/60">

                            <div>
                                <h3 className="text-sm font-bold text-white flex items-center gap-2">
                                    <FiCrop className="text-cyan-400" />

                                    Crop Logo
                                </h3>

                                <p className="text-[10px] text-slate-500 mt-1">
                                    Atur posisi gambar sesuai kebutuhan.
                                </p>
                            </div>

                            <button
                                type="button"
                                onClick={
                                    handleCancelCrop
                                }
                                className="
                                    w-8
                                    h-8
                                    rounded-lg
                                    flex
                                    items-center
                                    justify-center
                                    text-slate-400
                                    hover:text-white
                                    hover:bg-slate-800
                                    transition
                                "
                            >
                                <FiX size={18} />
                            </button>

                        </div>

                        {/* CROPPER */}

                        <div className="relative h-[400px] bg-black">

                            <Cropper
                                image={cropImage}
                                crop={crop}
                                zoom={zoom}
                                aspect={1}
                                cropShape="rect"
                                showGrid={true}
                                onCropChange={setCrop}
                                onCropComplete={
                                    onCropComplete
                                }
                                onZoomChange={setZoom}
                            />

                        </div>

                        {/* ZOOM */}

                        <div className="px-5 py-4 border-t border-slate-700/60">

                            <div className="flex items-center gap-3">

                                <FiZoomOut
                                    className="text-slate-500"
                                    size={16}
                                />

                                <input
                                    type="range"
                                    min={1}
                                    max={3}
                                    step={0.1}
                                    value={zoom}
                                    onChange={(e) =>
                                        setZoom(
                                            Number(
                                                e.target.value
                                            )
                                        )
                                    }
                                    className="
                                        flex-1
                                        accent-cyan-500
                                        cursor-pointer
                                    "
                                />

                                <FiZoomIn
                                    className="text-slate-500"
                                    size={16}
                                />

                                <span className="text-xs text-slate-400 w-10 text-right">
                                    {zoom.toFixed(1)}x
                                </span>

                            </div>
                        </div>

                        {/* FOOTER */}

                        <div className="flex justify-end gap-2 px-5 py-4 bg-slate-950/40 border-t border-slate-700/60">

                            <button
                                type="button"
                                onClick={
                                    handleCancelCrop
                                }
                                className="
                                    px-4
                                    py-2
                                    rounded-lg
                                    text-xs
                                    font-semibold
                                    text-slate-300
                                    border
                                    border-slate-700
                                    hover:bg-slate-800
                                    transition
                                "
                            >
                                Batal
                            </button>

                            <button
                                type="button"
                                onClick={
                                    handleApplyCrop
                                }
                                disabled={
                                    isCompressing
                                }
                                className="
                                    px-5
                                    py-2
                                    rounded-lg
                                    text-xs
                                    font-bold
                                    text-white
                                    flex
                                    items-center
                                    gap-2
                                    disabled:opacity-50
                                    disabled:cursor-not-allowed
                                "
                                style={{
                                    background:
                                        "linear-gradient(135deg, #0077B6, #00B4D8)",
                                }}
                            >
                                {isCompressing ? (
                                    <>
                                        <FiLoader className="animate-spin" />

                                        Memproses...
                                    </>
                                ) : (
                                    <>
                                        <FiCheck />

                                        Gunakan Gambar
                                    </>
                                )}
                            </button>

                        </div>

                    </div>
                </div>
            )}

            {/* ======================================================
                FULLSCREEN IMAGE
            ====================================================== */}

            {fullscreenImage && (
                <div
                    className="
                        fixed
                        inset-0
                        z-[9999]
                        bg-black/95
                        backdrop-blur-md
                        flex
                        items-center
                        justify-center
                        p-4
                    "
                    onClick={
                        handleCloseFullscreen
                    }
                >

                    {/* CLOSE */}

                    <button
                        type="button"
                        onClick={
                            handleCloseFullscreen
                        }
                        className="
                            absolute
                            top-5
                            right-5
                            z-20
                            w-10
                            h-10
                            rounded-xl
                            bg-white/10
                            hover:bg-white/20
                            border
                            border-white/20
                            flex
                            items-center
                            justify-center
                            text-white
                            transition
                        "
                    >
                        <FiX size={22} />
                    </button>

                    {/* IMAGE */}

                    <img
                        src={fullscreenImage}
                        alt="Logo Fullscreen"
                        className="
                            max-w-full
                            max-h-full
                            object-contain
                            select-none
                            rounded-lg
                            shadow-2xl
                        "
                        onClick={(e) =>
                            e.stopPropagation()
                        }
                    />

                </div>
            )}
        </>
    );
};
